/**
 * Record detail — /m/$moduleKey/$entityKey/$recordId
 *
 * Loads the entity definition and the single record, renders via
 * <EntityDetail>. Provides Edit and Delete actions.
 */

import type { ComputedFieldDefinition, FieldDefinition } from '@cloud/custom-modules'
import type { LayoutManifest } from '@cloud/modules'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, Edit, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { EntityDetail } from '@/components/custom/entity-detail'
import { type LayoutFieldMeta, LayoutRenderer } from '@/components/custom/layout-renderer'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/m/$moduleKey/$entityKey/$recordId/')({
  component: RecordDetailPage,
})

function RecordDetailPage() {
  const { moduleKey, entityKey, recordId } = Route.useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [deleteOpen, setDeleteOpen] = useState(false)

  const { data: entity, isLoading: entityLoading } = useQuery(
    orpc.modules.custom.entities.get.queryOptions({
      input: { moduleKey, key: entityKey },
    }),
  )

  const { data: record, isLoading: recordLoading } = useQuery(
    orpc.modules.custom.records.get.queryOptions({
      input: { moduleKey, entityKey, recordId },
    }),
  )

  // Fetch the saved layout for this entity. Falls back to the
  // synthesized fallback (one section, 2-column grid of every field)
  // when no override exists.
  const { data: layoutData } = useQuery(
    orpc.extensions.layouts.get.queryOptions({
      input: { moduleKey, entityKey, kind: 'detail' },
    }),
  )

  const deleteMutation = useMutation(
    orpc.modules.custom.records.delete.mutationOptions({
      onSuccess: () => {
        toast.success('Record deleted')
        void queryClient.invalidateQueries({
          queryKey: orpc.modules.custom.records.list.queryKey({
            input: { moduleKey, entityKey },
          }),
        })
        void navigate({
          to: '/m/$moduleKey/$entityKey',
          params: { moduleKey, entityKey },
        })
      },
      onError: (err) => toast.error(err.message || 'Failed to delete'),
    }),
  )

  if (entityLoading || recordLoading) {
    return (
      <div className="container mx-auto py-6 px-4 space-y-4 max-w-3xl">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (!entity || !record) {
    return (
      <div className="container mx-auto py-6 px-4">
        <h1 className="text-2xl font-bold">Record not found</h1>
      </div>
    )
  }

  // Primary display field: use the entity's primaryDisplayField if set,
  // otherwise fall back to the first text-ish field, otherwise the id.
  const primaryKey = entity.primaryDisplayField ?? null
  const title =
    (primaryKey && (record.data[primaryKey] as string)) || `${entity.name} #${recordId.slice(0, 8)}`

  return (
    <div className="container mx-auto py-6 px-4 max-w-3xl space-y-4">
      <Link
        to="/m/$moduleKey/$entityKey"
        params={{ moduleKey, entityKey }}
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4 mr-1" />
        Back to {entity.namePlural ?? `${entity.name}s`}
      </Link>

      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-bold">{title}</h1>
        <div className="flex gap-2">
          <Link
            to="/m/$moduleKey/$entityKey/$recordId/edit"
            params={{ moduleKey, entityKey, recordId }}
          >
            <Button variant="outline" size="sm">
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          </Link>
          <Button variant="outline" size="sm" onClick={() => setDeleteOpen(true)}>
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      {layoutData ? (
        <LayoutRenderer
          layout={layoutData.layout as unknown as LayoutManifest}
          record={record.data}
          coreFields={
            new Map<string, LayoutFieldMeta>(
              (entity.fields as FieldDefinition[]).map((f) => [
                f.key,
                {
                  label: f.label,
                  type: f.type,
                  options: f.options,
                  currency: f.currency,
                  allowOverride: f.allowOverride,
                },
              ]),
            )
          }
          extensionFields={new Map()}
          scenarioColumns={
            (entity.scenarios as { columns?: Array<{ key: string; label?: string }> } | null)
              ?.columns ?? undefined
          }
          onAction={(actionKey) => {
            // Action handlers are workflow-driven. The button click
            // surfaces a toast confirming the dispatch — the workflow
            // engine picks up the matching trigger event and runs the
            // configured steps. Real synchronous-action HTTP
            // invocation is a follow-up.
            toast.info(`Action dispatched: ${actionKey}`)
          }}
        />
      ) : (
        <EntityDetail
          title={entity.name}
          fields={entity.fields as FieldDefinition[]}
          computedFields={entity.computedFields as ComputedFieldDefinition[]}
          data={record.data}
        />
      )}

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {entity.name}?</DialogTitle>
            <DialogDescription>
              This will move the record to the trash. You can restore it later from the trash view.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => deleteMutation.mutate({ moduleKey, entityKey, recordId })}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
